import { formatTicker } from '@api';
import { dedupeArray, getTimeTillEndOfDayMs } from '@helpers';
import { useQueries } from '@tanstack/react-query';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { QUERY_KEY_INSIGHT } from '@terminal/react-query-hooks/insights/useInsight';
import { getInsight } from '@terminal/services/insights/insights-service';

import { normalizeFinancialStatementItems } from '../financial-statements-item/financialStatementUtils';
import type {
  ConversationInventory,
  FormattedConversationInventory,
} from '../services/inventory-service';

export const useFormattedInventory = (
  inventory: ConversationInventory | undefined
): FormattedConversationInventory => {
  const insightIds =
    inventory?.insight?.items?.map(item => item.id) ?? [];

  const insightQueryResults = useQueries({
    queries: insightIds.map(id => ({
      queryKey: [QUERY_KEY_INSIGHT, id],
      queryFn: () => getInsight(id),
      enabled: insightIds.length > 0,
      staleTime: getTimeTillEndOfDayMs(),
    })),
  });

  const insightTitles = Object.fromEntries(
    insightIds.map((id, i) => {
      const res = insightQueryResults[i]?.data;
      const title =
        res?.data?.title ?? res?.data?.simple_title ?? id;
      return [id, title];
    })
  );

  const calendarEarningsTags = dedupeArray(
    (inventory?.['economic-calendar']?.items ?? [])
      .map(i => i.entity_tag)
      .filter((tag): tag is string => !!tag)
  );
  const { data: calendarEarningsData } = useEntities({
    entityTags: calendarEarningsTags,
  });
  const economicCalendarEntityNames = Object.fromEntries(
    calendarEarningsData.map(e => [e.tag, e.name_short || e.name_full])
  );

  const entityTags = inventory?.entity?.items?.map(i => i.tag) ?? [];
  const snakes = inventory?.timeseries?.items?.map(i => i.snake) ?? [];
  const financialStatementItems = normalizeFinancialStatementItems(
    inventory?.['financial-statement']?.items ?? []
  );
  const financialStatementsEntityTags = dedupeArray(
    financialStatementItems.map(item => item.entityTag)
  );

  const snakesEntityTags = dedupeArray(
    snakes.map(snake => snake.split('.')[0])
  );
  const uniqueEntityTags = dedupeArray(entityTags);
  const { data: snakesData } = useEntities({ entityTags: snakesEntityTags });
  const { data: entitiesData } = useEntities({ entityTags: uniqueEntityTags });
  const { data: financialStatementsData } = useEntities({
    entityTags: financialStatementsEntityTags,
  });

  const formattedTickers = entitiesData.map(formatTicker);
  const formattedSnakes = snakesData.map(formatTicker);
  const formattedTickerByEntityTag = Object.fromEntries(
    financialStatementsData.map(entity => [
      entity.tag,
      formatTicker(entity),
    ])
  );
  const formattedFinancialStatementItems = financialStatementItems.map(
    item => ({
      tag: formattedTickerByEntityTag[item.entityTag] ?? item.entityTag,
      statement: item.statement,
    })
  );

  return {
    ...inventory,
    ...(formattedTickers.length > 0 && {
      entity: {
        items: formattedTickers.map(ticker => ({ tag: ticker })),
      },
    }),
    ...(formattedSnakes.length > 0 && {
      timeseries: {
        items: formattedSnakes.map(snake => ({ snake })),
      },
    }),
    ...(formattedFinancialStatementItems.length > 0 && {
      ['financial-statement']: {
        items: formattedFinancialStatementItems,
      },
    }),
    ...(Object.keys(insightTitles).length > 0 && { insightTitles }),
    ...(Object.keys(economicCalendarEntityNames).length > 0 && {
      economicCalendarEntityNames,
    }),
  };
};
